import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchProtectedFile } from "../utils/fileUrlHelper";

let pdfJsModulesPromise = null;

function loadPdfJsModules() {
  if (!pdfJsModulesPromise) {
    pdfJsModulesPromise = Promise.all([
      import("pdfjs-dist/build/pdf.min.mjs"),
      import("pdfjs-dist/build/pdf.worker.min.mjs"),
    ]).then(([pdfModule, workerModule]) => {
      // Capacitor/WKWebView, `capacitor://` altında module Worker başlatmayı bazı
      // iOS sürümlerinde reddedebiliyor. Fake worker yalnız PDF açıldığında kurulur;
      // PDF.js artık uygulamanın ilk açılış paketini ve web başlangıcını engellemez.
      if (typeof window !== "undefined" && !window.pdfjsWorker?.WorkerMessageHandler) {
        window.pdfjsWorker = { WorkerMessageHandler: workerModule.WorkerMessageHandler };
      }
      return pdfModule;
    }).catch((error) => {
      pdfJsModulesPromise = null;
      throw error;
    });
  }
  return pdfJsModulesPromise;
}

function PdfPage({ pdf, pageNumber, width, onReady, onError, label }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!pdf || !width) return undefined;
    let cancelled = false;
    let renderTask = null;
    let renderTimer = null;

    renderTimer = window.setTimeout(() => (async () => {
      const page = await pdf.getPage(pageNumber);
      if (cancelled) return;
      const baseViewport = page.getViewport({ scale: 1 });
      const cssWidth = Math.max(240, width);
      const cssScale = cssWidth / baseViewport.width;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const renderViewport = page.getViewport({ scale: cssScale * pixelRatio });
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;

      canvas.width = Math.floor(renderViewport.width);
      canvas.height = Math.floor(renderViewport.height);
      canvas.style.width = `${Math.floor(renderViewport.width / pixelRatio)}px`;
      canvas.style.height = `${Math.floor(renderViewport.height / pixelRatio)}px`;
      renderTask = page.render({ canvasContext: canvas.getContext("2d", { alpha: false }), viewport: renderViewport });
      await renderTask.promise;
      if (!cancelled) onReady?.(pageNumber);
    })().catch((error) => {
      if (!cancelled && pageNumber === 1) onError?.(error);
    }), 90);

    return () => {
      cancelled = true;
      window.clearTimeout(renderTimer);
      renderTask?.cancel?.();
    };
  }, [onError, onReady, pageNumber, pdf, width]);

  return <canvas ref={canvasRef} className="securePdfPage" role="img" aria-label={`${label} ${pageNumber}`} />;
}

export default function SecurePdfViewer({ src, title, lang = "tr", onReady, onError }) {
  const hostRef = useRef(null);
  const pagesRef = useRef(null);
  const firstReadyRef = useRef(false);
  const pinchRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [width, setWidth] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [fallbackUrl, setFallbackUrl] = useState("");

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const update = () => setWidth(Math.max(240, Math.floor(host.clientWidth - 16)));
    update();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(host);
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    const pages = pagesRef.current;
    if (!pages || !pdf) return undefined;
    const update = () => setContentHeight(Math.max(1, Math.ceil(pages.scrollHeight)));
    update();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(pages);
    const frame = window.requestAnimationFrame(update);
    return () => {
      observer?.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [pdf, width]);

  useEffect(() => {
    if (!src) return undefined;
    const abortController = new AbortController();
    firstReadyRef.current = false;
    pinchRef.current = null;
    setZoom(1);
    setContentHeight(0);
    setPdf(null);
    setFallbackUrl("");
    let task = null;
    let loadedPdf = null;
    let objectUrl = "";

    (async () => {
      const response = await fetchProtectedFile(src, {
        method: "GET",
        cache: "no-store",
        signal: abortController.signal,
      });
      if (!response.ok) throw new Error(`PDF HTTP ${response.status}`);
      const data = await response.arrayBuffer();
      if (!data.byteLength) throw new Error("PDF is empty");
      try {
        const { getDocument } = await loadPdfJsModules();
        task = getDocument({
          data: new Uint8Array(data),
          isEvalSupported: false,
          useWorkerFetch: false,
          useWasm: false,
        });
        const document = await task.promise;
        if (!abortController.signal.aborted) {
          loadedPdf = document;
          setPdf(document);
        }
      } catch (pdfError) {
        if (abortController.signal.aborted) return;
        // Bozuk olmayan ancak cihazdaki PDF.js motorunun işleyemediği belgelerde
        // WKWebView'in uygulama içi PDF motoruna, adresi görünmeyen blob ile düş.
        objectUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        setFallbackUrl(objectUrl);
      }
    })().catch((error) => {
      if (error?.name !== "AbortError") onError?.(error);
    });

    return () => {
      abortController.abort();
      task?.destroy?.();
      loadedPdf?.destroy?.();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [onError, src]);

  const pageReady = useCallback((pageNumber) => {
    if (pageNumber !== 1 || firstReadyRef.current) return;
    firstReadyRef.current = true;
    onReady?.();
  }, [onReady]);

  const onTouchStart = (event) => {
    const host = hostRef.current;
    if (!host || fallbackUrl) return;
    if (event.touches.length === 1) {
      pinchRef.current = {
        mode: "pan",
        startX: event.touches[0].clientX,
        startY: event.touches[0].clientY,
        scrollLeft: host.scrollLeft,
        scrollTop: host.scrollTop,
      };
      return;
    }
    if (event.touches.length !== 2) return;
    const rect = host.getBoundingClientRect();
    const viewportX = ((event.touches[0].clientX + event.touches[1].clientX) / 2) - rect.left;
    const viewportY = ((event.touches[0].clientY + event.touches[1].clientY) / 2) - rect.top;
    pinchRef.current = {
      mode: "pinch",
      distance: Math.hypot(event.touches[0].clientX - event.touches[1].clientX, event.touches[0].clientY - event.touches[1].clientY),
      zoom,
      viewportX,
      viewportY,
      contentX: (host.scrollLeft + viewportX) / zoom,
      contentY: (host.scrollTop + viewportY) / zoom,
    };
  };

  const onTouchMove = (event) => {
    const host = hostRef.current;
    const pinch = pinchRef.current;
    if (!host || !pinch) return;
    if (pinch.mode === "pan" && event.touches.length === 1) {
      event.preventDefault();
      host.scrollLeft = Math.max(0, pinch.scrollLeft - (event.touches[0].clientX - pinch.startX));
      host.scrollTop = Math.max(0, pinch.scrollTop - (event.touches[0].clientY - pinch.startY));
      return;
    }
    if (pinch.mode !== "pinch" || event.touches.length !== 2) return;
    event.preventDefault();
    const nextDistance = Math.hypot(event.touches[0].clientX - event.touches[1].clientX, event.touches[0].clientY - event.touches[1].clientY);
    const nextZoom = Math.max(1, Math.min(3, pinch.zoom * (nextDistance / Math.max(1, pinch.distance))));
    setZoom(nextZoom);
    window.requestAnimationFrame(() => {
      host.scrollLeft = Math.max(0, (pinch.contentX * nextZoom) - pinch.viewportX);
      host.scrollTop = Math.max(0, (pinch.contentY * nextZoom) - pinch.viewportY);
    });
  };

  const onTouchEnd = (event) => {
    if (event.touches.length === 0 || (pinchRef.current?.mode === "pinch" && event.touches.length < 2)) pinchRef.current = null;
  };

  return (
    <div ref={hostRef} className="securePdfViewport" aria-label={title} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd} onDoubleClick={() => setZoom((value) => value > 1 ? 1 : 2)}>
      {fallbackUrl && (
        <iframe
          className="securePdfNativeFallback"
          src={fallbackUrl}
          title={title}
          onLoad={onReady}
          onError={onError}
        />
      )}
      {pdf && (
        <div className="securePdfZoomSurface" style={{ width: `${Math.max(width, width * zoom)}px`, height: `${Math.max(contentHeight, contentHeight * zoom)}px` }}>
          <div ref={pagesRef} className="securePdfPages" style={{ width: `${width}px`, transform: `scale(${zoom})` }}>
            {Array.from({ length: pdf.numPages }, (_, index) => (
              <PdfPage
                key={index + 1}
                pdf={pdf}
                pageNumber={index + 1}
                width={width}
                onReady={pageReady}
                onError={onError}
                label={lang === "en" ? "Page" : "Sayfa"}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
